import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Typography } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  SearchOutlined,
  MailOutlined,
  BookOutlined,
  SettingOutlined,
  BellOutlined,
  BarChartOutlined,
  TeamOutlined,
  SoundOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../config/routes';
import { usePermission } from '../../hooks/usePermission';
import { useAuthStore } from '../../stores/authStore';
import { COLORS, GRADIENTS } from '../../constants/colors';
import type { MenuProps } from 'antd';

const { Sider } = Layout;
const { Title } = Typography;

interface SidebarProps {
  collapsed: boolean;
}

// 菜单项配置（带权限控制）
const getFilteredMenuItems = (isAdmin: boolean, isSuperAdmin: boolean): MenuProps['items'] => {
  const showAdminMenu = isSuperAdmin;
  
  const items: MenuProps['items'] = [
    {
      key: ROUTES.DASHBOARD,
      icon: <DashboardOutlined />,
      label: '工作台',
    },
    {
      key: 'proposals',
      icon: <FileTextOutlined />,
      label: '提案中心',
      children: [
        { key: ROUTES.CUSTOMERS, label: '客户管理' },
        { key: ROUTES.PROPOSALS, label: '提案列表' },
      ],
    },
    {
      key: 'scout',
      icon: <SearchOutlined />,
      label: '红人星探',
      children: [
        { key: ROUTES.SCOUT_SEARCH, label: '红人搜索' },
        { key: ROUTES.SCOUT_SCREENING, label: '筛选任务' },
      ],
    },
    {
      key: 'outreach',
      icon: <MailOutlined />,
      label: '红人邀约',
      children: [
        { key: ROUTES.OUTREACH_CAMPAIGNS, label: '邀约活动' },
        { key: ROUTES.OUTREACH_REPLIES, label: '回复监测' },
        { key: ROUTES.OUTREACH_EMAIL_ACCOUNTS, label: '渠道账号' },
      ],
    },
    {
      key: ROUTES.KNOWLEDGE,
      icon: <BookOutlined />,
      label: '知识库',
    },
    {
      key: ROUTES.NOTIFICATIONS,
      icon: <BellOutlined />,
      label: '通知中心',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
      children: [
        { key: ROUTES.SETTINGS_TENANT, label: '租户设置' },
        { key: ROUTES.SETTINGS_MEMBERS, label: '成员管理' },
        { key: ROUTES.SETTINGS_ROLES, label: '角色权限' },
        { key: ROUTES.SETTINGS_SUBSCRIPTION, label: '订阅管理' },
        { key: ROUTES.SETTINGS_USAGE, label: '用量统计' },
        { key: ROUTES.SETTINGS_DATA_EXPORT, label: '数据导出' },
        { key: ROUTES.SETTINGS_ACCOUNT_LIFECYCLE, label: '账户管理' },
      ],
    },
    // 平台管理菜单（仅超管可见）
    ...(showAdminMenu
      ? [
          {
            key: 'admin',
            icon: <BarChartOutlined />,
            label: '平台管理',
            children: [
              { key: ROUTES.ADMIN, label: '运营仪表盘' },
              { key: ROUTES.ADMIN_TENANTS, label: '租户管理' },
              { key: ROUTES.ADMIN_ANNOUNCEMENTS, label: '公告管理' },
            ],
          },
        ]
      : []),
  ];

  // 如果不是管理员，过滤设置菜单中的管理员专属项
  if (!isAdmin) {
    const settingsItem = items.find((item) => {
      if (item && typeof item === 'object' && 'key' in item) {
        return item.key === 'settings';
      }
      return false;
    }) as { key: string; icon: React.ReactNode; label: string; children: { key: string; label: string }[] } | undefined;

    if (settingsItem) {
      // 非管理员只能看到租户设置
      settingsItem.children = [
        { key: ROUTES.SETTINGS_TENANT, label: '租户设置' },
      ];
    }
  }

  return items;
};

export const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { isAdmin, isSuperAdmin } = usePermission();

  // 根据权限过滤菜单
  const menuItems = useMemo(() => getFilteredMenuItems(isAdmin, isSuperAdmin), [isAdmin, isSuperAdmin]);

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    return [location.pathname];
  };

  // 根据路径获取需要展开的子菜单
  const getDefaultOpenKeys = () => {
    const pathname = location.pathname;
    if (pathname.startsWith('/proposals')) return ['proposals'];
    if (pathname.startsWith('/scout')) return ['scout'];
    if (pathname.startsWith('/outreach')) return ['outreach'];
    if (pathname.startsWith('/settings')) return ['settings'];
    if (pathname.startsWith('/admin')) return ['admin'];
    return [];
  };

  // 初始化和路径变化时更新展开的子菜单
  useEffect(() => {
    const defaultOpenKeys = getDefaultOpenKeys();
    if (defaultOpenKeys.length > 0 && !collapsed) {
      setOpenKeys(defaultOpenKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, collapsed]);

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      theme="dark"
      style={{
        background: GRADIENTS.sidebar,
      }}
    >
      {/* Logo区域 */}
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {collapsed ? (
          <Title level={4} style={{ margin: 0, color: COLORS.white, fontWeight: 700 }}>
            N
          </Title>
        ) : (
          <Title level={4} style={{ margin: 0, color: COLORS.white, fontWeight: 600 }}>
            NexusInflu
          </Title>
        )}
      </div>
      {/* 菜单 */}
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={getSelectedKeys()}
        openKeys={collapsed ? [] : openKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          borderRight: 0,
          background: 'transparent',
        }}
      />
    </Sider>
  );
};
